import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';
import { API_BASE_URL, API_VERSION_PREFIX } from '../tokens/api-base-url.token';
import type { TokenResponse } from '../models';
import { FeedbackApiTokenStore } from './feedback-api-token-store';

/** Absolute URL of the token endpoint for a given API origin. */
export function feedbackApiTokenUrl(baseUrl: string): string {
  return `${baseUrl}/${API_VERSION_PREFIX}/token`;
}

/**
 * Fetches a replacement Feedback API token and stores it.
 *
 * Concurrent callers share one request: a page that fires several calls at once
 * will see them all fail with `401` together, and each would otherwise trigger
 * its own refresh. The in-flight observable is reused until it settles, so the
 * burst costs a single `GET /v1/token`.
 *
 * Used by {@link feedbackApiAuthInterceptor} when token refresh is enabled.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackApiTokenRefresher {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly tokenStore = inject(FeedbackApiTokenStore);

  private inFlightRefresh: Observable<string | null> | null = null;

  /**
   * The freshly-stored token, or null when the fetch failed or returned none.
   * Never errors — the caller's own failure is the one worth surfacing.
   */
  refresh(): Observable<string | null> {
    if (this.inFlightRefresh) {
      return this.inFlightRefresh;
    }

    this.inFlightRefresh = this.http.get<TokenResponse>(feedbackApiTokenUrl(this.baseUrl)).pipe(
      map((response) => {
        const freshToken = response?.token ?? null;
        if (freshToken) {
          this.tokenStore.set(freshToken);
        }
        return freshToken;
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.inFlightRefresh = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlightRefresh;
  }
}
